import type {
    GeoJSONPoint,
    Location,
    LocationGeometry,
    LocationMutatableData,
} from '@/types/locations'
import { GOOGLE_MAPS_API_KEY, PB_SERVER_ADDR } from './config'
import { errorToString } from './errors'
import { log } from './logger'
import { pb } from './pocketbase'
import { slugify } from './string'

// Default values for location forms. Matches MIN_GEOFENCE_RADIUS so newly
// created circles start at the smallest reliable size — admins can grow them
// from there.
export const DEFAULT_CIRCLE_RADIUS = 150 // meters
export { DEFAULT_LOCATION_NAME, displayLocationName } from './location-utils'

// Type guard functions (kept for consistency, but all locations are now Points)
export function isGeoJSONPoint(geometry: LocationGeometry): geometry is GeoJSONPoint {
    return geometry.type === 'Point'
}

// Helper functions
export function formatLocationGeometry(location: Location): string {
    const { geometry } = location
    const radius = geometry.radius || 0
    const [lat, lng] = geometry.coordinates || [0, 0]
    return `Circle at ${lat.toFixed?.(4)}, ${lng.toFixed?.(4)} (${radius}m radius)`
}

// Google Maps Static API thumbnail generation
export interface ThumbnailOptions {
    width?: number
    height?: number
    mapType?: 'roadmap' | 'satellite' | 'terrain' | 'hybrid'
    scale?: 1 | 2
    strokeColor?: string
    fillColor?: string
    strokeWeight?: number
    zoom?: number
}

export function generateLocationThumbnailUrl(
    location: { geometry: LocationGeometry },
    options: ThumbnailOptions = {}
): string {
    const {
        width = 400,
        height = 300,
        mapType = 'roadmap',
        scale = 1,
        strokeColor = '0x0000FF',
        fillColor = '0x0000FF33',
        strokeWeight = 2,
        zoom,
    } = options

    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap'
    const params = new URLSearchParams({
        size: `${width}x${height}`,
        maptype: mapType,
        scale: scale.toString(),
        key: GOOGLE_MAPS_API_KEY,
    })

    const { geometry } = location

    // All locations are now Point/Circle geometry
    const [lng, lat] = geometry.coordinates
    const radius = geometry.radius || DEFAULT_CIRCLE_RADIUS

    // Add center point
    params.append('center', `${lat},${lng}`)

    // Calculate zoom level based on radius if not provided
    if (zoom) {
        params.append('zoom', zoom.toString())
    } else {
        // Rough zoom calculation based on radius (meters to zoom level)
        const calculatedZoom = Math.max(1, Math.min(20, Math.round(14 - Math.log2(radius / 500))))
        params.append('zoom', calculatedZoom.toString())
    }

    // Add circle marker
    params.append('markers', `color:${strokeColor.replace('0x', '')}|size:mid|${lat},${lng}`)

    // For circles, we'll use a path to approximate the circle
    const circlePoints = generateCirclePoints(lat, lng, radius, 32)
    const pathParam = `color:${strokeColor}|weight:${strokeWeight}|fillcolor:${fillColor}|${circlePoints.join('|')}`
    params.append('path', pathParam)

    return `${baseUrl}?${params.toString()}`
}

// Helper function to generate circle points for approximating a circle
function generateCirclePoints(
    lat: number,
    lng: number,
    radius: number,
    segments: number = 32
): string[] {
    const points: string[] = []
    const earthRadius = 6371000 // Earth radius in meters

    for (let i = 0; i <= segments; i++) {
        const angle = (i * 2 * Math.PI) / segments

        // Calculate offset in degrees
        const deltaLat = ((radius * Math.cos(angle)) / earthRadius) * (180 / Math.PI)
        const deltaLng =
            ((radius * Math.sin(angle)) / (earthRadius * Math.cos((lat * Math.PI) / 180))) *
            (180 / Math.PI)

        const pointLat = lat + deltaLat
        const pointLng = lng + deltaLng

        points.push(`${pointLat},${pointLng}`)
    }

    return points
}

// Generate PocketBase file URL for location thumbnail
export function getLocationThumbnailUrl(location: Location, thumbSize?: string): string | null {
    if (!location.thumbnail) return null

    const baseUrl = `${PB_SERVER_ADDR}/api/files/locations/${location.id}/${location.thumbnail}`
    return thumbSize ? `${baseUrl}?thumb=${thumbSize}` : baseUrl
}

export async function uploadLocationThumbnail(
    location: { id: string; name: string; geometry: LocationGeometry },
    options: ThumbnailOptions = {}
) {
    try {
        const thumbnailUrl = generateLocationThumbnailUrl(location, options)

        const response = await fetch(thumbnailUrl)
        if (!response.ok) {
            throw new Error(`Failed to fetch thumbnail: ${response.status} ${response.statusText}`)
        }
        const blob = await response.blob()
        const filename = `location-${slugify(location.name)}-thumbnail.jpg`

        const formData = new FormData()
        formData.append('thumbnail', blob, filename)

        await pb.collection('locations').update(location.id, formData)
    } catch (error) {
        log.errorWithoutSentry('Failed to upload location thumbnail:', errorToString(error))
    }
}

// Default values utility for location forms
export function createDefaultLocationFormData(
    overrides: Partial<LocationMutatableData> = {}
): LocationMutatableData {
    return {
        id: '',
        name: '',
        description: '',
        geometry: {
            type: 'Point',
            coordinates: [0, 0],
            radius: DEFAULT_CIRCLE_RADIUS,
        },
        inactive: false,
        tags: [],
        ...overrides,
    }
}
